import Image from 'next/image'
import LegacyImage from 'next/legacy/image'
import { cmsQuery } from '@web/cms'
import { getRevalidateTime } from '@web/utils/get-revalidate-time'
import { IconArrowRight16 } from '@hashicorp/flight-icons/svg-react/arrow-right-16'
import { getMaxStaticPaths } from 'lib/get-max-static-paths'
import PageHeadTags, { PageHeadTagsProps } from 'components/page-head-tags'
import {
	MostRecentSystemsIntegratorsDocument,
	SystemsIntegratorPageDocument,
} from 'types/dato'
import PartnerSubnav from 'views/partners/components/subnav'

import { HDSPlusStandaloneLink } from '@hashicorp/react-mds/src/hds-plus/standalone-link'
import { trackGoal as trackFathomGoal } from 'fathom-client'
import type { InferGetStaticPropsType } from 'next'
import s from './style.module.css'
import { withStandardI18nParams } from 'lib/i18n/helpers/with-standard-i18n-params'
import { Card } from '@hashicorp/react-mds/src/components/card'

export default function SystemsIntegratorPage({
	systemsIntegrator,
}: InferGetStaticPropsType<typeof getStaticProps>) {
	const pageHeadTagProps = formatPageHeadTagProps(systemsIntegrator)
	const { regionsServed } = systemsIntegrator
	const globalRegions = Object.keys(regionsServed)

	return (
		<>
			<PartnerSubnav />
			<PageHeadTags {...pageHeadTagProps} />
			<section className={s.content}>
				<div className={s.hero}>
					<div className="g-grid-container">
						<div className={s.heroContainer}>
							<div className={s.heroContent}>
								{systemsIntegrator.tier && (
									<p className={s.eyebrow}>
										Partner Tier: {systemsIntegrator.tier}
									</p>
								)}
								<h1 className={s.systemsIntegratorName}>
									{systemsIntegrator.name}
								</h1>
								<p className={s.companyDescription}>
									{systemsIntegrator.description}
								</p>
							</div>
							<div className={s.heroSecondary}>
								<LogoContainer
									logo={systemsIntegrator.logo}
									className={s.desktopLogo}
									partnerWebsiteUrl={systemsIntegrator.websiteUrl}
								/>
								{systemsIntegrator.websiteUrl && (
									<div className={s.partnerWebLink}>
										<HDSPlusStandaloneLink
											text="Visit partner website"
											href={systemsIntegrator.websiteUrl}
											onClick={() => trackExternalPartnerWebsiteClick()}
											icon={
												<IconArrowRight16
													style={{ display: 'block' }}
													width="100%"
													height="100%"
												/>
											}
											iconPosition="trailing"
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<Image
						src={require('components/_page-components/partners/images/partner-hero-graphic.svg')}
						className={s.heroGraphic}
						alt=""
						width={210}
						height={362}
					/>
				</div>
				<div className={s.heroSecondary}>
					<LogoContainer
						logo={systemsIntegrator.logo}
						className={s.mobileLogo}
						partnerWebsiteUrl={systemsIntegrator.websiteUrl}
					/>
				</div>
				<div className={s.mainContent}>
					<section className={s.regionsServed}>
						<h2 className={s.regionsServedHeading}>Regions served</h2>
						{globalRegions.length > 0 ? (
							<ul className={s.globalRegionsList}>
								{globalRegions.map((globalRegion) => {
									const localRegions = regionsServed[globalRegion]

									return (
										<li key={globalRegion}>
											<h3 className={s.globalRegionHeading}>{globalRegion}</h3>
											<ul
												// style={{'--row-count': } as React.CSSProperties}
												className={s.localRegionsList}
											>
												{localRegions.map((localRegion) => {
													return (
														<li className={s.localRegion} key={localRegion}>
															{localRegion}
														</li>
													)
												})}
											</ul>
										</li>
									)
								})}
							</ul>
						) : null}
					</section>
					<section className={s.badges}>
						{/* TODO Replace text items in these lists with badges */}
						{/* TODO Uncomment once Systems Integrator Competencies are implemented in the SI records */}
						{systemsIntegrator.systemsIntegratorCompetencies?.length > 0 ? (
							<div className={s.badgesCategory}>
								<h2 className={s.badgesCategoryHeading}>
									Systems integrator competencies
								</h2>
								<ul className={s.badgesList}>
									{systemsIntegrator.systemsIntegratorCompetencies.map(
										({ badge }, stableIdx) => {
											return (
												// eslint-disable-next-line react/no-array-index-key
												<li key={stableIdx}>
													<LegacyImage
														src={badge.url}
														alt={badge.alt}
														width={127}
														height={147}
													/>
												</li>
											)
										}
									)}
								</ul>
							</div>
						) : null}
						{systemsIntegrator.awardsAndRecognition?.length > 0 ? (
							<div className={s.badgesCategory}>
								<h2 className={s.badgesCategoryHeading}>
									Awards & recognition
								</h2>
								<ul className={s.badgesList}>
									{systemsIntegrator.awardsAndRecognition.map(
										(highlight, stableIdx) => {
											return (
												// eslint-disable-next-line react/no-array-index-key
												<li key={stableIdx}>
													<LegacyImage
														src={highlight.url}
														alt={highlight.alt}
														width={127}
														height={147}
													/>
												</li>
											)
										}
									)}
								</ul>
							</div>
						) : null}
						{systemsIntegrator.certifiedImplementationProducts?.length > 0 ? (
							<div className={s.badgesCategory}>
								<h2 className={s.badgesCategoryHeading}>
									Certified implementation partner
								</h2>
								<ul className={s.badgesList}>
									{systemsIntegrator.certifiedImplementationProducts.map(
										(product) => {
											return (
												<li
													className={s.certificationBadge}
													key={product.product}
												>
													<LegacyImage
														src={product.badge.url}
														alt={product.badge.alt}
														width={127}
														height={147}
													/>
												</li>
											)
										}
									)}
								</ul>
							</div>
						) : null}
					</section>

					{systemsIntegrator.relatedResources.length > 0 ? (
						<section className={s.relatedResources}>
							<h2 className={s.relatedResourcesHeading}>Related resources</h2>
							<div className={s.relatedResourcesList}>
								{systemsIntegrator.relatedResources.map((resource) => {
									return (
										<Card
											key={resource.linkText}
											href={resource.url}
											content={{ heading: resource.linkText, showArrow: true }}
										/>
									)
								})}
							</div>
							<div className={s.relatedResourcesCta}>
								<HDSPlusStandaloneLink
									text="View all resources"
									href={`/resources`}
									icon={
										<IconArrowRight16
											style={{ display: 'block' }}
											width="100%"
											height="100%"
										/>
									}
									iconPosition="trailing"
								/>
							</div>
						</section>
					) : null}
				</div>
			</section>
		</>
	)
}

function LogoContainer({ logo, className, partnerWebsiteUrl }) {
	if (!logo) return null
	if (!partnerWebsiteUrl) {
		return (
			<div className={`${s.logoContainer} ${className}`}>
				<Image {...logo} alt={logo.alt || ''} />
			</div>
		)
	} else {
		return (
			<a
				className={`${s.logoContainer} ${className}`}
				href={partnerWebsiteUrl}
				onClick={() => trackExternalPartnerWebsiteClick()}
				rel="noreferrer"
				target="_blank"
			>
				<Image {...logo} alt={logo.alt || ''} />
			</a>
		)
	}
}

function trackExternalPartnerWebsiteClick() {
	// ID here is from hashicorp.com Fathom site - 'Visited external partner website' event
	trackFathomGoal('EUKEDSKC', 0)
}

export async function getStaticPaths() {
	const {
		data: { allSystemsIntegrators },
	} = await cmsQuery({
		query: MostRecentSystemsIntegratorsDocument,
		variables: { first: getMaxStaticPaths() },
	})

	return {
		paths: withStandardI18nParams(
			allSystemsIntegrators.map((partner) => ({
				params: {
					slug: partner.slug,
				},
			}))
		),
		fallback: 'blocking',
	}
}

export async function getStaticProps({ params: { slug } }) {
	// TODO Update Dato to include fields for the following, and fetch them here
	// Get competencies
	// Get badges:
	// - Systems Integrator Competency
	// - Awards & recognition
	// - Certified implementation partner
	// Get related resources

	const {
		data: { systemsIntegrator },
	} = await cmsQuery({
		query: SystemsIntegratorPageDocument,
		variables: {
			slug,
		},
	})

	if (!systemsIntegrator) {
		return {
			notFound: true,
		}
	}

	return {
		props: {
			systemsIntegrator: {
				...systemsIntegrator,
				regionsServed: formatRegionsServed(systemsIntegrator.regionsServed),
				tier: formatTier(systemsIntegrator.tier),
			},
		},
		revalidate: getRevalidateTime(),
	}
}

function formatPageHeadTagProps(
	systemsIntegrator: InferGetStaticPropsType<
		typeof getStaticProps
	>['systemsIntegrator']
): PageHeadTagsProps {
	const { name, description, seo } = systemsIntegrator
	const titleOverride = `${name} | HashiCorp Tech Partner`
	const descriptionOverride =
		description ||
		`HashiCorp partners with ${name}, making it easier for organizations to provision, secure, connect, and run their applications in any environment.`

	return {
		titleOverride,
		descriptionOverride,
		tags: seo,
	}
}

function formatRegionsServed(regionsServed) {
	const formattedRegionsServed = {}

	const regionMap = {
		Europe: 'EMEA',
		'Asia & Australia': 'APAC',
		Americas: 'AMER',
		Global: 'Global',
	}

	if (regionsServed?.length > 0) {
		regionsServed.forEach((region) => {
			const regionString = regionMap[region.globalRegion]

			if (!formattedRegionsServed[regionString]) {
				formattedRegionsServed[regionString] = []
			}

			formattedRegionsServed[regionString].push(region.localRegion)
		})
	}

	return formattedRegionsServed
}

export function formatTier(tiers: string[]): string | null {
	if (!tiers || !tiers.length) {
		return null
	}

	const tierOrder = ['Hyper-Specialized', 'Specialized', 'Distributor']

	const sortedTiers = tiers.sort((a, b) => {
		const aIndex = tierOrder.indexOf(a)
		const bIndex = tierOrder.indexOf(b)

		if (aIndex === -1) {
			return 1
		}

		if (bIndex === -1) {
			return -1
		}

		return aIndex - bIndex
	})

	return sortedTiers[0]
}
